/**
 * <DriveStatusIcon> — small camera-facing 3D icon mounted above a
 * module / mover when its drive status is in warning or error.
 *
 * Two variants:
 *   - 'warning'  → orange triangle (▲) with a black "!" inside
 *   - 'error'    → red disc with a white "!" inside
 *
 * Built from primitive geometry (ShapeGeometry for the triangle,
 * CircleGeometry for the disc) plus drei `<Text>` for the glyph,
 * wrapped in `<Billboard>` so the icon always reads horizontally
 * regardless of the camera orbit. Rendered with depthTest off + a
 * high renderOrder so the icon stays visible even when sitting
 * inside a module's GLB bounds.
 */

import React, { useMemo } from 'react';
import { Billboard, Text } from '@react-three/drei';
import { Shape } from 'three';

const WARNING_FILL = '#FFA000';
const WARNING_GLYPH = '#1a1a1a';
const ERROR_FILL = '#E03030';
const ERROR_GLYPH = '#ffffff';

interface DriveStatusIconProps {
  kind: 'warning' | 'error';
  positionMm: [number, number, number];
  /** Outer size (mm) — triangle base / disc diameter. Default 45. */
  sizeMm?: number;
}

export const DriveStatusIcon: React.FC<DriveStatusIconProps> = ({
  kind,
  positionMm,
  sizeMm = 45,
}) => {
  const triangleShape = useMemo(() => {
    if (kind !== 'warning') return null;
    const r = sizeMm / 2;
    // Equilateral-ish triangle pointing up, slightly squat for
    // optical balance with the "!" glyph inside.
    const s = new Shape();
    s.moveTo(0, r);
    s.lineTo(-r * 0.95, -r * 0.7);
    s.lineTo(r * 0.95, -r * 0.7);
    s.lineTo(0, r);
    return s;
  }, [kind, sizeMm]);

  return (
    <Billboard position={positionMm} follow>
      {kind === 'warning' && triangleShape && (
        <>
          <mesh renderOrder={9990}>
            <shapeGeometry args={[triangleShape]} />
            <meshBasicMaterial
              color={WARNING_FILL}
              toneMapped={false}
              depthTest={false}
              transparent
              opacity={0.95}
            />
          </mesh>
          <Text
            position={[0, -sizeMm * 0.05, 0.5]}
            fontSize={sizeMm * 0.6}
            color={WARNING_GLYPH}
            anchorX="center"
            anchorY="middle"
            renderOrder={9991}
            // Drei's <Text> sets these via its own material; passing
            // them through keeps the glyph on top of the triangle.
            depthOffset={-1}
          >
            !
          </Text>
        </>
      )}
      {kind === 'error' && (
        <>
          <mesh renderOrder={9990}>
            <circleGeometry args={[sizeMm / 2, 32]} />
            <meshBasicMaterial
              color={ERROR_FILL}
              toneMapped={false}
              depthTest={false}
              transparent
              opacity={0.95}
            />
          </mesh>
          <Text
            position={[0, 0, 0.5]}
            fontSize={sizeMm * 0.7}
            color={ERROR_GLYPH}
            anchorX="center"
            anchorY="middle"
            renderOrder={9991}
            depthOffset={-1}
          >
            !
          </Text>
        </>
      )}
    </Billboard>
  );
};
